import type { AchadoDeRevisao } from '@mymaxx/shared';
import { Check, ChevronDown, SpellCheck, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { atendimentoApi } from '../lib/atendimento-api';

/**
 * O corretor do campo de digitação do atendente.
 *
 * Propõe, nunca conserta sozinho (decisão de 08/10). O que sai daqui vai para o cliente
 * assinado pela empresa — e um corretor que reescreve no automático troca o erro de quem
 * escreveu pelo erro de quem corrige. Medido com o corretor de verdade: sem a lista de
 * jargão da casa, "drop" vira "do" e "splitter" vira "split ter".
 *
 * Mostra as correções como etiquetas acima do campo, e não como sublinhado ondulado dentro
 * dele. Duas razões: o navegador já sublinha erro de ortografia sozinho, e o que ele NÃO
 * faz — concordância — é justamente o que precisa ser lido para ser avaliado. "o cabos →
 * os cabos" numa etiqueta se decide num olhar; sublinhado exige passar o mouse e esperar.
 */

/** Quanto tempo de teclado parado antes de perguntar ao corretor. */
const ESPERA_MS = 600;

/** Abaixo disso não vale perguntar: "ok" e "sim" não têm o que corrigir. */
const MINIMO_CARACTERES = 4;

const CHAVE_PREFERENCIA = 'mymaxx:corretor';

export function corretorLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE_PREFERENCIA) !== 'desligado';
  } catch {
    return true;
  }
}

function definirCorretor(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE_PREFERENCIA, ligado ? 'ligado' : 'desligado');
  } catch {
    /* navegador sem armazenamento: a escolha vale só nesta sessão */
  }
}

/**
 * Pergunta ao corretor quando o teclado para.
 *
 * O pedido em andamento é cancelado quando o texto muda: sem isso, a resposta de um texto
 * antigo chegaria depois e apontaria para posições que não existem mais — e a correção
 * cairia no meio de outra palavra.
 */
export function useRevisao(texto: string, ligado: boolean) {
  const [achados, setAchados] = useState<AchadoDeRevisao[]>([]);
  const [disponivel, setDisponivel] = useState(false);

  useEffect(() => {
    if (!ligado || texto.trim().length < MINIMO_CARACTERES) {
      setAchados([]);
      return;
    }
    let valeu = true;
    const id = setTimeout(() => {
      void atendimentoApi
        .revisar(texto)
        .then((r) => {
          if (!valeu) return;
          setAchados(r.achados);
          setDisponivel(r.disponivel);
        })
        .catch(() => {
          // Corretor fora do ar não é assunto de quem está atendendo: some e pronto.
          if (valeu) setAchados([]);
        });
    }, ESPERA_MS);
    return () => {
      valeu = false;
      clearTimeout(id);
    };
  }, [texto, ligado]);

  return { achados, disponivel };
}

/**
 * Aplica uma correção e devolve o texto novo.
 *
 * Exportada para ser testável sem tela: é a única parte disto que, errando, manda ao
 * cliente uma frase picada.
 */
export function aplicar(texto: string, achado: AchadoDeRevisao, sugestao: string): string {
  return texto.slice(0, achado.inicio) + sugestao + texto.slice(achado.inicio + achado.tamanho);
}

/**
 * Aplica tudo de uma vez, **de trás para frente**.
 *
 * Da frente para trás, a primeira troca já deslocaria todas as posições seguintes — e a
 * segunda correção cairia alguns caracteres fora do lugar. Isso não dá erro: dá uma frase
 * estragada enviada ao cliente.
 */
export function aplicarTudo(texto: string, achados: AchadoDeRevisao[]): string {
  return [...achados]
    .sort((a, b) => b.inicio - a.inicio)
    .reduce((t, a) => (a.sugestoes[0] ? aplicar(t, a, a.sugestoes[0]) : t), texto);
}

export function BarraDoCorretor({
  texto,
  achados,
  onTexto,
  ligado,
  onLigado,
}: {
  texto: string;
  achados: AchadoDeRevisao[];
  onTexto: (texto: string) => void;
  ligado: boolean;
  onLigado: (ligado: boolean) => void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  /**
   * Qual achado está mostrando as alternativas.
   *
   * Existe porque numa concordância as opções dizem coisas OPOSTAS: "o cabos" vira "o
   * cabo" ou "os cabos", e aplicar sempre a primeira mudaria o que o atendente contou ao
   * cliente — um cabo ou vários (visto na verificação, 08/10).
   *
   * Abrem na própria linha, e não num menu suspenso: a barra fica no rodapé da tela, e
   * menu posicionado por cima de área que rola é onde esta base já tropeçou duas vezes.
   */
  const [abertas, setAbertas] = useState<string | null>(null);

  if (!ligado) {
    return (
      <button
        type="button"
        onClick={() => {
          definirCorretor(true);
          onLigado(true);
        }}
        className="flex items-center gap-1.5 self-start px-1 pb-1 text-[11px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
      >
        <SpellCheck size={12} /> Ligar o corretor
      </button>
    );
  }
  if (achados.length === 0) return null;

  return (
    <div ref={caixa} className="flex flex-wrap items-center gap-1.5 px-1 pb-1.5">
      <SpellCheck size={13} className="shrink-0 text-marca-600 dark:text-marca-300" />
      {achados.map((a) => {
        const chave = `${a.inicio}:${a.tamanho}`;
        const original = texto.slice(a.inicio, a.inicio + a.tamanho);

        // Alternativas abertas: uma etiqueta por opção, para a escolha ser de quem escreve.
        if (abertas === chave) {
          return a.sugestoes.map((s) => (
            <button
              key={`${chave}:${s}`}
              type="button"
              title={a.mensagem}
              onClick={() => {
                setAbertas(null);
                onTexto(aplicar(texto, a, s));
              }}
              className="flex items-center gap-1 rounded-full border border-marca-400 bg-marca-50 px-2 py-0.5 text-[11px] dark:border-marca-500 dark:bg-neutral-800"
            >
              <span className="text-neutral-400 line-through">{original}</span>
              <span>→</span>
              <span className="font-medium">{s}</span>
            </button>
          ));
        }

        return (
          <span key={chave} className="flex items-center">
            <button
              type="button"
              title={a.mensagem}
              onClick={() => onTexto(aplicar(texto, a, a.sugestoes[0]!))}
              className={`flex items-center gap-1 border border-neutral-200 py-0.5 pl-2 text-[11px] hover:border-marca-400 hover:bg-marca-50 dark:border-neutral-700 dark:hover:bg-neutral-800 ${
                a.sugestoes.length > 1 ? 'rounded-l-full pr-1.5' : 'rounded-full pr-2'
              }`}
            >
              <span className="text-neutral-400 line-through">{original}</span>
              <span>→</span>
              <span className="font-medium">{a.sugestoes[0]}</span>
            </button>
            {a.sugestoes.length > 1 && (
              <button
                type="button"
                title={`Outras ${a.sugestoes.length - 1} opções`}
                aria-label={`Outras opções para "${original}"`}
                onClick={() => setAbertas(chave)}
                className="rounded-r-full border border-l-0 border-neutral-200 py-0.5 pr-1.5 pl-0.5 text-neutral-400 hover:border-marca-400 hover:bg-marca-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
              >
                <ChevronDown size={11} />
              </button>
            )}
          </span>
        );
      })}

      {achados.length > 1 && (
        <button
          type="button"
          onClick={() => onTexto(aplicarTudo(texto, achados))}
          className="flex items-center gap-1 rounded-full bg-marca-600 px-2 py-0.5 text-[11px] text-white hover:bg-marca-700"
        >
          <Check size={11} /> Corrigir {achados.length}
        </button>
      )}

      <button
        type="button"
        title="Desligar o corretor"
        aria-label="Desligar o corretor"
        onClick={() => {
          definirCorretor(false);
          onLigado(false);
        }}
        className="ml-auto rounded p-0.5 text-neutral-300 hover:text-neutral-500"
      >
        <X size={12} />
      </button>
    </div>
  );
}
